import { useCallback, useEffect, useState } from 'react';
import type { AccountCoverageTotals, ListAccountPipelineOutput } from '@amzn/ivthub-client';
import { ListAccountPipelineCommand, getIvtClient } from '../../client/IvtHubClient';
import { type DenialKind, denialKindOf } from '../../utils/authorization';
import type { WindowRange } from './components/windowPresets';

export interface AccountPipelineState {
  data: ListAccountPipelineOutput | null;
  /**
   * `coverage` of the last response under the current scope MINUS the filter, for the Part 1
   * card. The figures ignore the filter, so a filter change keeps them on screen rather than
   * blanking them for the length of the request, as `data` is.
   */
  coverage: AccountCoverageTotals | undefined;
  loading: boolean;
  error: string | null;
  /** Same split as {@link useAssetFlow}: a 403 must not offer a retry button. */
  denial: DenialKind | null;
  reload: () => void;
  /**
   * Back to page one and refetch, discarding every token walked so far — for after a WRITE.
   *
   * ⚠️ Not the same as {@link reload}, which keeps the tokens so a failed page can be retried in
   * place. After an add or a delete the account set itself has changed, and the tokens are keyset
   * cursors (last ARR, last account id) cut on the OLD set: replaying them would repeat an account
   * that moved up a page, or skip one pushed down across the boundary.
   */
  restart: () => void;
  /** 1-based, and back to 1 whenever the scope changes. */
  pageIndex: number;
  /** Pages walked so far — the only ones that can be returned to. */
  pagesCount: number;
  hasNextPage: boolean;
  nextPage: () => void;
  /** Jump to an already-walked page; anything beyond {@link pagesCount} is a no-op. */
  goToPage: (index: number) => void;
}

/**
 * 我的灯塔客户 and 圈外归仓 from one operation, one hook instance per block.
 *
 * ⭐ THE TOKEN NEVER LEAVES THIS HOOK. Its fingerprint covers the window, the industries, the
 * block flag, the relevance floor and the filter, so replaying one after any of those change is a 400 rather
 * than a silent re-base. Hence a render-phase scope reset, not an effect: an effect would let one
 * request go out against the old scope's token first.
 *
 * `industries` is always exactly `[industry]`: the STO view queries ONE industry at a time, so
 * the contract's "omit it = every industry I may see" form is deliberately unreachable. A `null`
 * industry is "not resolved yet" (see useAssetFlow) and sends nothing while `loading` stays up.
 * `minRelevanceScore` is OMITTED rather than defaulted — the service owns its 3.
 *
 * `propertyFilter` (DAT-9223) is the block's filter as the JSON string the operation takes,
 * `undefined` for none. It is part of the token's scope like the rest, so a new filter restarts
 * the walk at page one: a token minted under another filter is a 400.
 */
export function useAccountPipeline(
  range: WindowRange,
  industry: string | null,
  lighthouse: boolean,
  minScore: number | null,
  enabled = true,
  propertyFilter?: string,
): AccountPipelineState {
  const [data, setData] = useState<ListAccountPipelineOutput | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [denial, setDenial] = useState<DenialKind | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { startDate, endDate } = range;

  // Everything the token's fingerprint is bound to; a change restarts the walk.
  const coverageKey = `${startDate}\u0000${endDate}\u0000${industry ?? ''}\u0000${lighthouse}\u0000${minScore ?? ''}`;
  const scopeKey = `${coverageKey}\u0000${propertyFilter ?? ''}`;
  const [coverage, setCoverage] = useState<{ key: string; value: AccountCoverageTotals | undefined } | null>(null);
  const [scope, setScope] = useState(scopeKey);
  /** `tokens[i]` is what fetches page i+1; page one needs none. */
  const [tokens, setTokens] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(1);

  const staleScope = scope !== scopeKey;
  if (staleScope) {
    setScope(scopeKey);
    setTokens([undefined]);
    setPageIndex(1);
    // Rows are scope-labelled, so they are dropped rather than shown under the new scope. A
    // transient failure keeps them (see the catch): there is no new scope to mislabel them as.
    setData(null);
  }
  // Read through the reset rather than off state: the setters above have not taken effect on the
  // render that performs it.
  const activeIndex = staleScope ? 1 : pageIndex;
  const activeToken = staleScope ? undefined : tokens[activeIndex - 1];
  const activeData = staleScope ? null : data;

  useEffect(() => {
    let cancelled = false;
    // Cleared rather than left alone: switching away mid-flight cancels the settle below, which
    // would otherwise strand `loading` at true.
    if (!enabled) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    // No industry, no request: this is what keeps an "all industries" page off the wire.
    if (industry === null) return;
    // `denial` is cleared on SUCCESS, not here — clearing it at request start makes refused
    // figures reachable for the whole duration of the next request.
    (async () => {
      try {
        const out = await getIvtClient().send(
          new ListAccountPipelineCommand({
            startDate,
            endDate,
            lighthouse,
            // Always exactly one — `industry` is non-null past the guard above.
            industries: [industry],
            // Omitted for the default, so the server's 3 stays the single source of it.
            ...(minScore !== null && { minRelevanceScore: minScore }),
            // Absent means NO filter; the block's default is sent explicitly by the caller.
            ...(propertyFilter !== undefined && { propertyFilter }),
            // Verbatim, and absent on page one.
            ...(activeToken !== undefined && { nextToken: activeToken }),
          }),
        );
        if (!cancelled) {
          setData(out);
          setCoverage({ key: coverageKey, value: out.coverage });
          setDenial(null);
        }
      } catch (e) {
        // Classified before the message is flattened: `denialKindOf` reads fields off the thrown
        // object that `(e as Error).message` does not carry.
        if (!cancelled) {
          const kind = denialKindOf(e);
          setDenial(kind);
          // Keep the previous page's rows through a transient failure, but DROP them on a
          // denial: the cache is what makes refused figures reachable at all.
          if (kind !== null) {
            setData(null);
            setCoverage(null);
          }
          setError((e as Error).message ?? 'Unknown error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Listed individually rather than via `scopeKey`, so the join cannot mask one of them.
    // `coverageKey` is derived from deps already listed.
  }, [startDate, endDate, industry, lighthouse, minScore, propertyFilter, activeToken, activeIndex, reloadKey, enabled]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  const restart = useCallback(() => {
    setTokens([undefined]);
    setPageIndex(1);
    // Still bumped when already on page one, where the two resets change no effect dependency.
    setReloadKey((k) => k + 1);
  }, []);

  const nextToken = activeData?.nextToken;
  const hasNextPage = nextToken != null && nextToken !== '';

  const nextPage = useCallback(() => {
    if (nextToken == null || nextToken === '') return;
    setTokens((prev) => (prev.length > activeIndex ? prev : [...prev, nextToken]));
    setPageIndex(activeIndex + 1);
  }, [nextToken, activeIndex]);

  const goToPage = useCallback(
    (index: number) => {
      // Forward-only medium: only a page whose token is already in hand is reachable, because
      // a token names one position under one scope and cannot be synthesised for another.
      if (index >= 1 && index <= tokens.length) setPageIndex(index);
    },
    [tokens.length],
  );

  return {
    data: activeData,
    coverage: coverage?.key === coverageKey ? coverage.value : undefined,
    // Raised here too: the effect runs after the commit, so the reset render would otherwise pair
    // null rows with `loading` false and paint the empty state for a frame.
    loading: loading || (staleScope && enabled),
    error,
    denial,
    reload,
    restart,
    pageIndex: activeIndex,
    pagesCount: staleScope ? 1 : tokens.length,
    hasNextPage,
    nextPage,
    goToPage,
  };
}
